import { useEffect, useState } from 'react';

import { api } from '../api/client';
import { getTemplate } from '../templates';
import type { ChartData, SavedChart } from '../types';
import { CustomHtmlChart } from './CustomHtmlChart';

/**
 * Fetches a chart's data and draws it — the one place that knows how to turn a
 * saved chart into pixels, whether it is a template or generated HTML.
 *
 * `overrideParams` carries the app filter the platform passed in (app_id /
 * app_ids), merged over the chart's own saved params.
 */
interface Props {
  chart: SavedChart;
  overrideParams?: Record<string, string>;
}

export const ChartRenderer = ({ chart, overrideParams }: Props) => {
  const [data, setData] = useState<ChartData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const params = { ...chart.params, ...overrideParams };
  // Params are an object, so a stable key avoids refetching on every render.
  const paramKey = JSON.stringify(params);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);

    api
      .query({ source: chart.source, chartId: chart.chartId, params })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart.source, chart.chartId, paramKey]);

  if (error) {
    return (
      <div className="chart-error">
        <strong>Chart unavailable</strong>
        <span>{error}</span>
      </div>
    );
  }

  if (!data) return <div className="chart-loading">Loading…</div>;

  if (chart.kind === 'custom' && chart.html) {
    return <CustomHtmlChart html={chart.html} data={data} title={chart.name} />;
  }

  return getTemplate(chart.template).render(data);
};
